import { Component, OnDestroy, OnInit, HostBinding, HostListener, ViewEncapsulation } from '@angular/core';
import { MediaObserver } from '@angular/flex-layout';

import * as _ from 'lodash';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { CoreSidebarService } from '@core/components/core-sidebar/core-sidebar.service';
import { CoreConfigService } from '@core/services/config.service';
import { CoreMediaService } from '@core/services/media.service';
import { Router, RoutesRecognized } from '@angular/router';
import { HttpErrorResponse } from '@angular/common/http';
import { AuthenticationService } from '../../../core/service';
import { UserAuthenticatedDTO } from '../../../core/requests/UserAuthenticatedDTO';
import { User } from '../../../core/models';
import { CoreTranslationService } from '@core/services/translation.service';
import {CategoriesService} from '../../../core/service/categories.service';
import {BenefitsService} from '../../../core/service/benefits.service';
import {SelfServiceAdminService} from '../../../core/service/selfServiceAdmin.service';
import {environment} from "../../../../environments/environment";
import {DashboardService} from "../../../core/service/dashboard.service";

@Component({
  selector: 'app-navbar',
  templateUrl: './navbar.component.html',
  styleUrls: ['./navbar.component.scss'],
  providers: [
    TranslatePipe
  ],
  encapsulation: ViewEncapsulation.None
})
export class NavbarComponent implements OnInit, OnDestroy {

  public profilePictureSrc = 'assets/images/avatars/no-user-image-icon.jpg';
  public currentUserInfo: User;
  public currentUser: UserAuthenticatedDTO;
  public subdomain: string;
  public categories: Array<any> = new Array<any>();
  public selfServiceEnabled = false;
  public selfServiceLink = '';
  public faqLink = '';
  public faqLinkIsSet = false;

  constructor(
    private _router: Router,
    private _authenticationService: AuthenticationService,
    private _coreConfigService: CoreConfigService,
    private _coreMediaService: CoreMediaService,
    private _coreSidebarService: CoreSidebarService,
    private _mediaObserver: MediaObserver,
    public _translateService: TranslateService,
    private _coreTranslationService: CoreTranslationService,
    private _categoriesService: CategoriesService,
    private _benefitsService: BenefitsService,
    private _selfServiceAdminService: SelfServiceAdminService,
    private _dashboardService: DashboardService
  ) {
    this._authenticationService.currentUser.subscribe(x => (this.currentUser = x));
    this._authenticationService.currentUserInfo.subscribe(x => {
      this.currentUserInfo = x;
      this.setupSelfService(this.currentUser, this.subdomain);
      if (this.currentUserInfo) {
        if (this.currentUserInfo.profilePicturePhotoUrl) {
          this.profilePictureSrc = this.currentUserInfo.profilePicturePhotoUrl;
        } else {
          this.profilePictureSrc = 'assets/images/avatars/no-user-image-icon.jpg';
        }
      }
    });
    this.subdomain = localStorage.getItem('currentUserSubdomainMybenefits');
    this._router.events.subscribe(val => {
      if (val instanceof RoutesRecognized) {
        this.subdomain = val.state.root.firstChild.params['subdomain'];
      }
    });
    this.languageOptions = {
      en: {
        title: 'English',
        flag: 'us'
      },
      fr: {
        title: 'French',
        flag: 'fr'
      },
      de: {
        title: 'German',
        flag: 'de'
      },
      pt: {
        title: 'Portuguese',
        flag: 'pt'
      }
    };
    this._authenticationService.currentUserCompanyInfo.subscribe(x => {
      if (x && x.companyHeaderTitle) {
        this.companyHeaderTitle = x.companyHeaderTitle;
      }
    });

    // Set the private defaults
    this._unsubscribeAll = new Subject();
  }

  public horizontalMenu: boolean;
  public hiddenMenu: boolean;

  public coreConfig: any;
  public currentSkin: string;
  public prevSkin: string;

  public languageOptions: any;
  public navigation: any;
  public selectedLanguage: any;
  public username: string;
  public email: string;
  public companyHeaderTitle: string;


  @HostBinding('class.fixed-top')
  public isFixed = false;

  @HostBinding('class.navbar-static-style-on-scroll')
  public windowScrolled = false;

  // Private
  private _unsubscribeAll: Subject<any>;

  // Add .navbar-static-style-on-scroll on scroll using HostListener & HostBinding
  @HostListener('window:scroll', [])
  onWindowScroll() {
    if (
      (window.pageYOffset || document.documentElement.scrollTop || document.body.scrollTop > 100) &&
            this.coreConfig.layout.navbar.type === 'navbar-static-top'
    ) {
      this.windowScrolled = true;
    } else if (
      (this.windowScrolled && window.pageYOffset) ||
            document.documentElement.scrollTop ||
            document.body.scrollTop < 10
    ) {
      this.windowScrolled = false;
    }
  }

  // Public Methods
  // -----------------------------------------------------------------------------------------------------

  /**
     * Toggle sidebar open
     *
     * @param key
     */
  toggleSidebar(key): void {
    this._coreSidebarService.getSidebarRegistry(key).toggleOpen();
  }

  /**
     * Set the language
     *
     * @param language
     */
  setLanguage(language): void {
    // Set the selected language for the navbar on change
    this.selectedLanguage = language;
    this._coreTranslationService.setActiveLang(language);

    // Use the selected language id for translations
    this._translateService.use(language);
  }

  /**
     * Logout method
     */
  logout() {
    this._authenticationService.logout()
      .subscribe({
        next: value => this.onLogoutSuccess(),
        error: err => this.onLogoutFail(err)
      });
  }

  public onLogoutSuccess() {
    this._categoriesService.clearCache();
    this._benefitsService.clearCache();
    this._dashboardService.clearCache();
  }

  public onLogoutFail(error: HttpErrorResponse) {
  }

  // Lifecycle Hooks
  // -----------------------------------------------------------------------------------------------------

  /**
     * On init
     */
  ngOnInit(): void {
    this.faqLink = environment.FAQ_URL;
    this.faqLinkIsSet = this.faqLink && this.faqLink.length > 0 && this.faqLink.startsWith("https");
    // get the currentUser details from localStorage
    this._authenticationService.currentUser.subscribe(x => (this.currentUser = x));
    if (this._authenticationService.currentUserValue) {
      this.email = this._authenticationService.currentUserValue.email;
      this.username = this._authenticationService.currentUserValue.firstName + ' '
                + this._authenticationService.currentUserValue.lastName;
    }
    // Subscribe to the config changes
    this._coreConfigService.config.pipe(takeUntil(this._unsubscribeAll)).subscribe(config => {
      this.coreConfig = config;
      this.horizontalMenu = config.layout.type === 'horizontal';
      this.hiddenMenu = config.layout.menu.hidden === true;
      this.currentSkin = config.layout.skin;

      // Fix: for vertical layout if default navbar fixed-top than set isFixed = true
      if (this.coreConfig.layout.type === 'vertical') {
        setTimeout(() => {
          if (this.coreConfig.layout.navbar.type === 'fixed-top') {
            this.isFixed = true;
          }
        }, 0);
      }
    });

    // Horizontal Layout Only: Add class fixed-top to navbar below large screen
    if (this.coreConfig.layout.type === 'horizontal') {
      // On every media(screen) change
      this._coreMediaService.onMediaUpdate.pipe(takeUntil(this._unsubscribeAll)).subscribe(() => {
        const isFixedTop = this._mediaObserver.isActive('bs-gt-xl');
        if (isFixedTop) {
          this.isFixed = false;
        } else {
          this.isFixed = true;
        }
      });
    }

    // Set the selected language from default languageOptions
    setTimeout(() => {
      this.selectedLanguage = this._coreTranslationService.getActiveLang();
    });
  }

  /**
     * On destroy
     */
  ngOnDestroy(): void {
    // Unsubscribe from all subscriptions
    this._unsubscribeAll.next(true);
    this._unsubscribeAll.complete();
  }

  private setupSelfService(currentUser: UserAuthenticatedDTO, subdomain: string) {
    if (!currentUser.roles.find(r => r.toLowerCase() === 'hr' || r.toLowerCase() === 'admin')) {
      this.selfServiceEnabled = false;
    } else {
      this.selfServiceEnabled = this._selfServiceAdminService.isEnabled(subdomain);
      this.selfServiceLink = this._selfServiceAdminService.getJumpUrl(currentUser.accessToken, currentUser.refreshToken);
    }

  }
}
